import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Trophy, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(username, password);
      toast.success(`Welcome back, ${user.username}!`);
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/trader');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid credentials';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="flex items-center justify-center p-8 bg-[#09090B]">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardHeader className="space-y-1 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Trophy className="w-8 h-8 text-blue-500" />
              <span className="font-heading text-2xl font-bold text-zinc-100">Bookmaker</span>
            </div>
            <CardTitle className="text-xl font-heading text-zinc-100">Welcome back</CardTitle>
            <CardDescription className="text-zinc-400">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded" data-testid="login-error">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="username" className="text-zinc-300">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="bg-black border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:ring-blue-600"
                  data-testid="username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-black border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:ring-blue-600"
                  data-testid="password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1691388205947-15659f1a4820?crop=entropy&cs=srgb&fm=jpg&q=85')"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-black/40" />
      </div>
    </div>
  );
}
